import React from "react";
import type {
  AppUser,
  UserProfile,
  UserRelationship,
  ToastType,
} from "../types";
import { View } from "../types";
import {
  acceptFriendRequest,
  cancelFriendRequest,
  declineFriendRequest,
  getCrewMemberProfiles,
  removeFriend,
  sendFriendRequest,
  subscribeToCrewRelationships,
  unblockUser,
} from "../services/users";
import { resolveRoddoDarkMode } from "../constants/theme";
import { ShareIcon } from "./icons";
import { buildCrewInviteShareUrl, share } from "../utils/share";
import ConfirmationModal from "./ConfirmationModal";

type Props = {
  currentUser: UserProfile;
  users?: Record<string, AppUser>;
  navigateTo: (view: View, state?: Record<string, any>) => void;
  addToast: (message: string, type?: ToastType) => void;
  isDarkMode?: boolean;
  onBack?: () => void;
};

const DEFAULT_AVATAR = "/avatar-default.png";
const clean = (v: any) => String(v ?? "").trim();


const crewTactilePolishStyles = `
  [data-roddo-crew-polish] button,
  [data-roddo-crew-polish] [role="button"],
  [data-roddo-crew-polish] input,
  [data-roddo-crew-polish] textarea {
    -webkit-tap-highlight-color: transparent;
  }

  [data-roddo-crew-polish] button,
  [data-roddo-crew-polish] [role="button"] {
    transform: translateZ(0);
    transition:
      transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 180ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 220ms ease,
      background 220ms ease;
  }

  [data-roddo-crew-polish] button:hover,
  [data-roddo-crew-polish] [role="button"]:hover {
    transform: translateY(-1px) translateZ(0);
    filter: brightness(1.025) saturate(1.03);
  }

  [data-roddo-crew-polish] button:active,
  [data-roddo-crew-polish] [role="button"]:active {
    transform: translateY(1px) scale(0.985) translateZ(0);
  }

  [data-roddo-crew-polish] input:focus-visible,
  [data-roddo-crew-polish] textarea:focus-visible,
  [data-roddo-crew-polish] button:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--crew-focus, #2F80ED) 22%, transparent),
      0 0 18px color-mix(in srgb, var(--crew-focus, #2F80ED) 12%, transparent);
  }

  [data-roddo-crew-polish] .crew-card {
    transition:
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 220ms ease,
      box-shadow 260ms ease;
  }

  [data-roddo-crew-polish] .crew-card:hover {
    transform: translateY(-1px);
    filter: saturate(1.02) brightness(1.01);
  }

  @media (prefers-reduced-motion: reduce) {
    [data-roddo-crew-polish] button,
    [data-roddo-crew-polish] [role="button"],
    [data-roddo-crew-polish] .crew-card {
      transition: none !important;
      transform: none !important;
      filter: none !important;
    }
  }
`;


const otherUid = (rel: UserRelationship, uid: string) => {
  const ids = Array.isArray(rel.participantIds)
    ? rel.participantIds
    : [rel.requesterId, rel.recipientId].filter(Boolean);
  return ids.find((id) => id && id !== uid) || "";
};

const displayNameFor = (user: any, uid: string) =>
  clean(user?.username) ||
  clean(user?.handle) ||
  clean(user?.displayName) ||
  clean(user?.name) ||
  (uid ? `Angler ${uid.slice(0, 4)}` : "Angler");

const avatarFor = (user: any) =>
  clean(user?.avatar) ||
  clean(user?.avatarUrl) ||
  clean(user?.photoURL) ||
  DEFAULT_AVATAR;

const CrewSettingsView: React.FC<Props> = ({
  currentUser,
  users = {},
  navigateTo,
  addToast,
  isDarkMode: explicitIsDarkMode,
  onBack,
}) => {
  const uid = clean((currentUser as any)?.uid);
  const [relationships, setRelationships] = React.useState<UserRelationship[]>([]);
  const [relationshipProfiles, setRelationshipProfiles] = React.useState<Record<string, AppUser>>({});
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [anglerSearch, setAnglerSearch] = React.useState("");
  const [searchBusyId, setSearchBusyId] = React.useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = React.useState<{
    relId: string;
    targetUid: string;
    name: string;
  } | null>(null);
  const [confirmUnblock, setConfirmUnblock] = React.useState<{
    relId: string;
    targetUid: string;
    name: string;
  } | null>(null);
  const [confirmCancel, setConfirmCancel] = React.useState<{
    relId: string;
    targetUid: string;
    name: string;
  } | null>(null);

  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);

  React.useEffect(() => {
    if (!uid) return;
    return subscribeToCrewRelationships(uid, setRelationships, (err) =>
      console.warn("[CrewSettingsView] crew subscription warning", err),
    );
  }, [uid]);

  React.useEffect(() => {
    if (!uid || !relationships.length) {
      setRelationshipProfiles({});
      return;
    }

    const targetUids = Array.from(
      new Set(
        relationships
          .map((rel) => otherUid(rel, uid))
          .filter(Boolean),
      ),
    );

    if (!targetUids.length) {
      setRelationshipProfiles({});
      return;
    }

    let cancelled = false;

    getCrewMemberProfiles(targetUids)
      .then((profiles) => {
        if (!cancelled) setRelationshipProfiles(profiles || {});
      })
      .catch((err) => {
        console.warn("[CrewSettingsView] profile hydration warning", err);
        if (!cancelled) setRelationshipProfiles({});
      });

    return () => {
      cancelled = true;
    };
  }, [uid, relationships]);

  const accepted = React.useMemo(
    () => relationships.filter((r) => r.status === "accepted"),
    [relationships],
  );

  const incoming = React.useMemo(
    () =>
      relationships.filter(
        (r) => r.status === "pending" && r.recipientId === uid,
      ),
    [relationships, uid],
  );

  const outgoing = React.useMemo(
    () =>
      relationships.filter(
        (r) => r.status === "pending" && r.requesterId === uid,
      ),
    [relationships, uid],
  );

  const blocked = React.useMemo(
    () =>
      relationships.filter(
        (r) => r.status === "blocked" && r.blockedBy === uid,
      ),
    [relationships, uid],
  );

  const relationshipByTarget = React.useMemo(() => {
    const map = new Map<string, UserRelationship>();
    relationships.forEach((rel) => {
      const targetUid = otherUid(rel, uid);
      if (targetUid) map.set(targetUid, rel);
    });
    return map;
  }, [relationships, uid]);

  const anglerResults = React.useMemo(() => {
    const q = clean(anglerSearch).toLowerCase().replace(/^@/, "");
    if (q.length < 2) return [];

    return Object.entries(users)
      .map(([key, user]) => {
        const anyUser = user as any;
        const targetUid = clean(anyUser?.uid) || key;
        const name = displayNameFor(anyUser, targetUid);
        const username = clean(anyUser?.username) || clean(anyUser?.handle);

        const haystack = [
          targetUid,
          name,
          username,
          anyUser?.displayName,
          anyUser?.usernameLower,
          anyUser?.handleLower,
          anyUser?.email,
        ]
          .map((v) => clean(v).toLowerCase().replace(/^@/, ""))
          .filter(Boolean)
          .join(" ");

        return { targetUid, user: anyUser, name, username, haystack };
      })
      .filter(
        (row) =>
          row.targetUid &&
          row.targetUid !== uid &&
          row.haystack.includes(q),
      )
      .slice(0, 7);
  }, [anglerSearch, uid, users]);

  const userFor = React.useCallback(
    (targetUid: string) =>
      (relationshipProfiles[targetUid] as any) ||
      (users[targetUid] as any) ||
      null,
    [relationshipProfiles, users],
  );

  const openProfile = (targetUid: string) => {
    if (!targetUid) return;
    navigateTo(View.PublicProfile, { targetUserId: targetUid });
  };

  const handleBack = React.useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    navigateTo(View.Crew);
  }, [navigateTo, onBack]);

  const handleShareCrewInvite = React.useCallback(async () => {
    if (!uid) {
      addToast("Sign in before sharing your crew invite.", "error");
      return;
    }

    try {
      const result = await share({
        url: buildCrewInviteShareUrl(uid),
      });

      if (result?.ok && result.method === "clipboard") {
        addToast("Crew invite link copied.", "success");
      }
    } catch (err) {
      console.error("[CrewSettingsView] share crew invite failed", err);
      addToast("Could not share the crew invite.", "error");
    }
  }, [uid, currentUser, addToast]);

  const run = async (
    id: string,
    action: () => Promise<void>,
    ok: string,
  ) => {
    try {
      setBusyId(id);
      await action();
      addToast(ok, "success");
    } catch (err) {
      console.error("[CrewSettingsView] action failed", err);
      addToast("Crew setting failed. Try again.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveCrewmate = (
    relId: string,
    targetUid: string,
    name: string,
  ) => {
    setConfirmRemove({ relId, targetUid, name });
  };

  const confirmRemoveCrewmate = async () => {
    const pending = confirmRemove;
    if (!pending) return;

    await run(
      pending.relId,
      () => removeFriend(uid, pending.targetUid),
      `${pending.name} removed from your crew.`,
    );
    setConfirmRemove(null);
  };

  const confirmUnblockUser = async () => {
    const pending = confirmUnblock;
    if (!pending) return;

    await run(
      pending.relId,
      () => unblockUser(uid, pending.targetUid),
      `${pending.name} unblocked.`,
    );
    setConfirmUnblock(null);
  };

  const confirmCancelCrewRequest = async () => {
    const pending = confirmCancel;
    if (!pending) return;

    await run(
      pending.relId,
      () => cancelFriendRequest(uid, pending.targetUid),
      `Crew request to ${pending.name} cancelled.`,
    );
    setConfirmCancel(null);
  };

  const handleSendCrewRequest = async (
    targetUid: string,
    name: string,
  ) => {
    const cleanTarget = clean(targetUid);
    if (!uid || !cleanTarget || uid === cleanTarget) return;

    const rel = relationshipByTarget.get(cleanTarget);

    if (rel?.status === "accepted") {
      addToast(`${name} is already in your crew.`, "info");
      return;
    }

    if (rel?.status === "pending") {
      addToast(
        rel.requesterId === uid
          ? "Crew request already sent."
          : "They already sent you a crew request.",
        "info",
      );
      return;
    }

    try {
      setSearchBusyId(cleanTarget);
      await sendFriendRequest(uid, cleanTarget);
      addToast(`Crew pass sent to ${name}.`, "success");
      setAnglerSearch("");
    } catch (err) {
      console.error("[CrewSettingsView] send crew request failed", err);
      addToast("Could not send crew request. Try again.", "error");
    } finally {
      setSearchBusyId(null);
    }
  };

  const styles = React.useMemo(() => {
    const pageText = isDarkMode ? "#F2F6FC" : "#30271E";
    const softText = isDarkMode ? "#A8B4C7" : "#6E6255";
    const faintText = isDarkMode ? "#74839A" : "#8B7D6D";
    return {
      pageText,
      softText,
      faintText,
      heroBg: isDarkMode ? "#0A1120" : "#FBF5ED",
      shellBg: isDarkMode ? "#0D1625" : "#FFF9F2",
      cardBg: isDarkMode ? "#121C2D" : "#F5EEE5",
      rowBg: isDarkMode ? "#101A2A" : "rgba(255,252,247,0.92)",
      border: isDarkMode ? "rgba(132,151,190,0.14)" : "rgba(74,58,42,0.14)",
      borderStrong: isDarkMode ? "rgba(132,151,190,0.20)" : "rgba(74,58,42,0.22)",
      shadow: isDarkMode ? "0 4px 14px rgba(0,0,0,.16)" : "0 4px 14px rgba(62,45,28,.06)",
      primaryBg: isDarkMode ? "#8A70F2" : "#2F80ED",
      primaryText: "#FFFFFF",
      accent: isDarkMode ? "#8A70F2" : "#2F80ED",
      accentSoft: isDarkMode ? "rgba(138,112,242,.10)" : "rgba(47,128,237,.09)",
      accentBorder: isDarkMode ? "rgba(112,87,232,.28)" : "rgba(47,128,237,.22)",
      accentText: isDarkMode ? "#D9D2FF" : "#1F68B8",
      seaBg: isDarkMode ? "rgba(112,87,232,.10)" : "rgba(47,128,237,.08)",
      seaText: isDarkMode ? "#D9D2FF" : "#1F68B8",
      greenBg: isDarkMode ? "rgba(95,208,162,.10)" : "rgba(25,123,88,.08)",
      greenText: isDarkMode ? "#8FE0BE" : "#197B58",
      dangerBg: isDarkMode ? "rgba(255,154,165,.08)" : "rgba(166,61,73,.06)",
      dangerText: isDarkMode ? "#FF9AA5" : "#A63D49",
      goldBg: isDarkMode ? "rgba(242,184,75,.08)" : "rgba(138,103,24,.06)",
      goldText: isDarkMode ? "#F5CE7A" : "#8A6718",
    };
  }, [isDarkMode]);

  const buttonBase: React.CSSProperties = {
    borderColor: styles.border,
    transition:
      "transform 180ms ease, background 220ms ease, color 220ms ease, border-color 220ms ease, opacity 180ms ease",
  };

  const CrewPassRow = ({
    rel,
    mode,
  }: {
    rel: UserRelationship;
    mode: "accepted" | "incoming" | "outgoing";
  }) => {
    const targetUid = otherUid(rel, uid);
    const user = userFor(targetUid) as any;
    const name = displayNameFor(user, targetUid);
    const avatar = avatarFor(user);
    const isBusy = busyId === rel.id;

    const status =
      mode === "accepted"
        ? {
            label: "Crew",
            icon: "",
            bg: styles.seaBg,
            color: styles.seaText,
          }
        : mode === "incoming"
          ? {
              label: "Incoming",
              icon: "",
              bg: styles.primaryBg,
              color: styles.primaryText,
            }
          : {
              label: "Sent",
              icon: "",
              bg: styles.cardBg,
              color: styles.softText,
            };

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => openProfile(targetUid)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openProfile(targetUid);
          }
        }}
        className="cursor-pointer rounded-[12px] border p-3 transition"
        style={{
          background: styles.rowBg,
          borderColor: styles.border,
          boxShadow: isDarkMode
            ? "inset 0 1px 0 rgba(255,255,255,0.035)"
            : "inset 0 1px 0 rgba(255,255,255,0.72)",
        }}
        aria-label={`Open ${name}'s profile`}
        title={`Open ${name}'s profile`}
      >
        <div className="flex items-center gap-3">
          <div
            className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full border-2 bg-white/10 shadow-[0_5px_0_rgba(0,0,0,0.12)]"
            style={{ borderColor: styles.borderStrong }}
            aria-hidden="true"
          >
            <img
              src={avatar}
              alt=""
              className="h-full w-full object-cover"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div
              className="truncate text-sm font-black"
              style={{ color: styles.pageText }}
            >
              {name}
            </div>

            <div
              className="mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em]"
              style={{
                background: status.bg,
                borderColor: styles.border,
                color: status.color,
              }}
            >
              <span>{status.icon}</span>
              <span>{status.label}</span>
            </div>
          </div>

          {mode === "accepted" ? (
            <button
              type="button"
              disabled={isBusy}
              onClick={(event) => {
                event.stopPropagation();
                void handleRemoveCrewmate(rel.id, targetUid, name);
              }}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-lg font-black transition disabled:opacity-60"
              style={{
                ...buttonBase,
                background: styles.dangerBg,
                color: styles.dangerText,
              }}
              aria-label={`Remove ${name} from your crew`}
              title={`Remove ${name} from your crew`}
            >
              {isBusy ? "…" : "−"}
            </button>
          ) : null}
        </div>

        {mode === "incoming" ? (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={isBusy}
              onClick={(event) => {
                event.stopPropagation();
                void run(
                  rel.id,
                  () => acceptFriendRequest(uid, targetUid),
                  `${name} joined your crew.`,
                );
              }}
              className="rounded-[10px] border px-3 py-2 text-xs font-black transition disabled:opacity-60"
              style={{
                ...buttonBase,
                background: styles.greenBg,
                color: styles.greenText,
              }}
            >
              {isBusy ? "..." : "Accept"}
            </button>

            <button
              type="button"
              disabled={isBusy}
              onClick={(event) => {
                event.stopPropagation();
                void run(
                  rel.id,
                  () => declineFriendRequest(uid, targetUid),
                  "Crew request declined.",
                );
              }}
              className="rounded-[10px] border px-3 py-2 text-xs font-black transition disabled:opacity-60"
              style={{
                ...buttonBase,
                background: styles.dangerBg,
                color: styles.dangerText,
              }}
            >
              Decline
            </button>
          </div>
        ) : null}

        {mode === "outgoing" ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <div
              className="min-w-0 text-xs font-bold leading-5"
              style={{ color: styles.softText }}
            >
              Waiting for {name} to accept.
            </div>

            <button
              type="button"
              disabled={isBusy}
              onClick={(event) => {
                event.stopPropagation();
                setConfirmCancel({ relId: rel.id, targetUid, name });
              }}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-[18px] font-black disabled:opacity-60"
              style={{
                ...buttonBase,
                background: styles.dangerBg,
                color: styles.dangerText,
              }}
              aria-label={`Cancel crew request to ${name}`}
              title={`Cancel crew request to ${name}`}
            >
              {isBusy ? "…" : "×"}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const EmptyState = ({
    title,
    body,
  }: {
    title: string;
    body: string;
  }) => (
    <div
      className="rounded-[12px] border border-dashed p-4 text-sm font-bold leading-5"
      style={{
        background: styles.rowBg,
        borderColor: styles.border,
        color: styles.softText,
      }}
    >
      <div
        className="font-black"
        style={{ color: styles.pageText }}
      >
        {title}
      </div>
      <div className="mt-1">{body}</div>
    </div>
  );

  return (
    <div
      data-roddo-crew-polish
      className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-3 pb-6 pt-3"
      style={{
        color: styles.pageText,
        ["--crew-focus" as any]: isDarkMode ? "#7657E8" : "#2F80ED",
      }}
    >
      <style>{crewTactilePolishStyles}</style>
      <section
        className="border-b px-1 pb-4 pt-1"
        style={{ borderColor: styles.border }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[24px] font-black leading-tight tracking-tight">Crew Settings</h1>
            <p className="mt-1 text-sm font-semibold" style={{ color: styles.softText }}>
              Manage trusted anglers, requests, invitations, and privacy.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => void handleShareCrewInvite()}
              className="grid h-10 w-10 place-items-center rounded-[10px] border"
              style={{ background: styles.cardBg, borderColor: styles.border, color: styles.pageText }}
              aria-label="Share crew invite"
              title="Share crew invite"
            >
              <ShareIcon className="h-[17px] w-[17px] opacity-80" />
            </button>
            <button
              type="button"
              onClick={handleBack}
              className="grid h-10 w-10 place-items-center rounded-[10px] border text-2xl font-black leading-none"
              style={{ background: styles.cardBg, borderColor: styles.border, color: styles.pageText }}
              aria-label="Back"
              title="Back"
            >
              ‹
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-black">
          <span>{accepted.length} <span style={{ color: styles.softText }}>Crew</span></span>
          <span>{incoming.length} <span style={{ color: styles.softText }}>Requests</span></span>
          <span>{outgoing.length} <span style={{ color: styles.softText }}>Sent</span></span>
          <span>{blocked.length} <span style={{ color: styles.softText }}>Blocked</span></span>
        </div>
      </section>

      <section
        className="rounded-[18px] border p-3.5"
        style={{
          background: styles.shellBg,
          borderColor: styles.border,
          boxShadow: styles.shadow,
        }}
      >
        <div className="mb-3">
          <h2
            className="text-sm font-black uppercase tracking-[0.16em]"
            style={{ color: styles.softText }}
          >
            Find an Angler
          </h2>
          <p
            className="mt-1 text-xs font-semibold"
            style={{ color: styles.faintText }}
          >
            Search by display name, handle, or email from loaded profiles.
          </p>
        </div>

        <div className="relative">
          <input
            value={anglerSearch}
            onChange={(e) => setAnglerSearch(e.target.value)}
            placeholder="Search username or angler name..."
            className="roddo-mobile-form w-full rounded-[12px] border px-4 py-3 pr-11 text-sm font-bold outline-none transition focus:-translate-y-0.5"
            style={{
              background: isDarkMode ? styles.rowBg : "rgba(255,255,255,.82)",
              borderColor: styles.border,
              color: styles.pageText,
            }}
          />
          <span
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm"
            style={{ color: styles.softText }}
          >
            🔎
          </span>
        </div>

        {clean(anglerSearch).length >= 2 ? (
          <div className="mt-3 space-y-2">
            {anglerResults.length ? (
              anglerResults.map(
                ({ targetUid, user, name, username }) => {
                  const rel = relationshipByTarget.get(targetUid);
                  const isAccepted = rel?.status === "accepted";
                  const isPending = rel?.status === "pending";
                  const disabled =
                    isAccepted ||
                    isPending ||
                    searchBusyId === targetUid;

                  const label = isAccepted
                    ? "Crew"
                    : isPending
                      ? rel?.requesterId === uid
                        ? "Sent"
                        : "Incoming"
                      : "Send pass";

                  return (
                    <div
                      key={targetUid}
                      role="button"
                      tabIndex={0}
                      onClick={() => openProfile(targetUid)}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter" ||
                          event.key === " "
                        ) {
                          event.preventDefault();
                          openProfile(targetUid);
                        }
                      }}
                      className="flex cursor-pointer items-center gap-3 rounded-[12px] border p-2.5 transition"
                      style={{
                        background: styles.rowBg,
                        borderColor: styles.border,
                      }}
                      aria-label={`Open ${name}'s profile`}
                      title={`Open ${name}'s profile`}
                    >
                      <div
                        className="h-12 w-12 shrink-0 overflow-hidden rounded-[12px] border-2 bg-white/10 shadow-[0_4px_0_rgba(0,0,0,0.12)]"
                        style={{ borderColor: styles.borderStrong }}
                      >
                        <img
                          src={avatarFor(user)}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div
                          className="truncate text-sm font-black"
                          style={{ color: styles.pageText }}
                        >
                          {name}
                        </div>
                        <div
                          className="truncate text-[11px] font-bold"
                          style={{ color: styles.softText }}
                        >
                          {username
                            ? `@${username.replace(/^@/, "")}`
                            : "Roddo angler"}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={disabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleSendCrewRequest(
                            targetUid,
                            name,
                          );
                        }}
                        className="rounded-[10px] border px-3 py-2 text-xs font-black transition disabled:opacity-70"
                        style={{
                          ...buttonBase,
                          background:
                            isAccepted || isPending
                              ? styles.cardBg
                              : styles.greenBg,
                          color:
                            isAccepted || isPending
                              ? styles.softText
                              : styles.greenText,
                        }}
                      >
                        {searchBusyId === targetUid ? "..." : label}
                      </button>
                    </div>
                  );
                },
              )
            ) : (
              <EmptyState
                title="No anglers found"
                body="Try a different name or handle."
              />
            )}
          </div>
        ) : null}
      </section>

      <section
        className="rounded-[18px] border p-3.5"
        style={{
          background: styles.shellBg,
          borderColor: styles.border,
          boxShadow: styles.shadow,
        }}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2
              className="text-sm font-black uppercase tracking-[0.16em]"
              style={{ color: styles.softText }}
            >
              Crew Management
            </h2>
            <p
              className="mt-1 text-xs font-bold"
              style={{ color: styles.faintText }}
            >
              Approve, manage, and review private crew passes.
            </p>
          </div>

          <span
            className="rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em]"
            style={{
              background: styles.cardBg,
              borderColor: styles.border,
              color: styles.softText,
            }}
          >
            Private
          </span>
        </div>

        <div className="space-y-5">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3
                className="text-xs font-black uppercase tracking-[0.14em]"
                style={{ color: styles.softText }}
              >
                Incoming Requests
              </h3>
              <span
                className="text-xs font-black"
                style={{ color: styles.faintText }}
              >
                {incoming.length}
              </span>
            </div>

            {incoming.length ? (
              <div className="space-y-2.5">
                {incoming.map((rel) => (
                  <CrewPassRow
                    key={rel.id}
                    rel={rel}
                    mode="incoming"
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No one at the gate"
                body="Incoming crew requests will show up here."
              />
            )}
          </div>

          <div
            className="h-px"
            style={{ background: styles.border }}
          />

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3
                className="text-xs font-black uppercase tracking-[0.14em]"
                style={{ color: styles.softText }}
              >
                Your Crew
              </h3>
              <span
                className="text-xs font-black"
                style={{ color: styles.faintText }}
              >
                {accepted.length}
              </span>
            </div>

            {accepted.length ? (
              <div className="space-y-2.5">
                {accepted.map((rel) => (
                  <CrewPassRow
                    key={rel.id}
                    rel={rel}
                    mode="accepted"
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                title="Crew dock is empty"
                body="Search above to send your first crew pass."
              />
            )}
          </div>

          <div
            className="h-px"
            style={{ background: styles.border }}
          />

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3
                className="text-xs font-black uppercase tracking-[0.14em]"
                style={{ color: styles.softText }}
              >
                Sent Passes
              </h3>
              <span
                className="text-xs font-black"
                style={{ color: styles.faintText }}
              >
                {outgoing.length}
              </span>
            </div>

            {outgoing.length ? (
              <div className="space-y-2.5">
                {outgoing.map((rel) => (
                  <CrewPassRow
                    key={rel.id}
                    rel={rel}
                    mode="outgoing"
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No pending passes"
                body="Sent Crew requests will wait here until accepted."
              />
            )}
          </div>
        </div>
      </section>

      <section
        className="rounded-[18px] border p-3.5"
        style={{
          background: styles.shellBg,
          borderColor: styles.border,
          boxShadow: styles.shadow,
        }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2
              className="text-sm font-black uppercase tracking-[0.16em]"
              style={{ color: styles.softText }}
            >
              Blocked Anglers
            </h2>
            <p
              className="mt-1 text-xs font-semibold leading-5"
              style={{ color: styles.faintText }}
            >
              Blocked anglers cannot use your private Crew connection or send new Crew messages.
            </p>
          </div>

          <span
            className="rounded-full border px-2.5 py-1 text-[10px] font-black"
            style={{
              background: styles.dangerBg,
              borderColor: styles.border,
              color: styles.dangerText,
            }}
          >
            {blocked.length}
          </span>
        </div>

        {blocked.length ? (
          <div className="space-y-2">
            {blocked.map((rel) => {
              const targetUid = otherUid(rel, uid);
              const user = userFor(targetUid) as any;
              const name = displayNameFor(user, targetUid);
              const avatar = avatarFor(user);
              const isBusy = busyId === rel.id;

              return (
                <div
                  key={rel.id}
                  className="flex items-center gap-3 rounded-[14px] px-2.5 py-2"
                  style={{ background: styles.rowBg }}
                >
                  <button
                    type="button"
                    onClick={() => openProfile(targetUid)}
                    className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full"
                    aria-label={`Open ${name}'s profile`}
                    title={`Open ${name}'s profile`}
                  >
                    <img src={avatar} alt="" className="h-full w-full object-cover" />
                  </button>

                  <button
                    type="button"
                    onClick={() => openProfile(targetUid)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="truncate text-sm font-black" style={{ color: styles.pageText }}>
                      {name}
                    </div>
                    <div className="mt-0.5 text-[10px] font-black uppercase tracking-[0.12em]" style={{ color: styles.dangerText }}>
                      Blocked
                    </div>
                  </button>

                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => setConfirmUnblock({ relId: rel.id, targetUid, name })}
                    className="rounded-[10px] border px-3 py-2 text-xs font-black disabled:opacity-60"
                    style={{
                      ...buttonBase,
                      background: styles.cardBg,
                      color: styles.pageText,
                    }}
                  >
                    {isBusy ? "..." : "Unblock"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="rounded-[14px] px-3.5 py-3 text-xs font-semibold"
            style={{ background: styles.rowBg, color: styles.softText }}
          >
            No blocked anglers.
          </div>
        )}
      </section>

      <section
        className="rounded-[18px] border p-3.5"
        style={{
          background: styles.shellBg,
          borderColor: styles.border,
          boxShadow: styles.shadow,
        }}
      >
        <div className="flex gap-3">
          <div
            className="grid h-12 w-12 shrink-0 place-items-center rounded-[10px] border text-xl"
            style={{
              background: styles.seaBg,
              borderColor: styles.border,
            }}
          >
            🔐
          </div>

          <div>
            <h2
              className="text-sm font-black uppercase tracking-[0.16em]"
              style={{ color: styles.softText }}
            >
              Privacy
            </h2>

            <p
              className="mt-2 text-sm font-semibold leading-6"
              style={{ color: styles.softText }}
            >
              Crew boards and private messages stay separate from Baleen Bay.
              Removing a crewmate closes future private Crew access. Blocking also
              stops new Crew messages until you choose to unblock that angler.
            </p>
          </div>
        </div>
      </section>

      <ConfirmationModal
        isOpen={!!confirmRemove}
        onClose={() => {
          if (!busyId) setConfirmRemove(null);
        }}
        onConfirm={() => {
          void confirmRemoveCrewmate();
        }}
        title={confirmRemove ? `Remove ${confirmRemove.name}?` : "Remove crewmate?"}
        message="They will lose access to this private Crew connection and new Crew messages will stop."
        confirmText={busyId ? "Removing..." : "Remove"}
        cancelText="Cancel"
        isDarkMode={isDarkMode}
      />

      <ConfirmationModal
        isOpen={!!confirmUnblock}
        onClose={() => {
          if (!busyId) setConfirmUnblock(null);
        }}
        onConfirm={() => {
          void confirmUnblockUser();
        }}
        title={confirmUnblock ? `Unblock ${confirmUnblock.name}?` : "Unblock angler?"}
        message="They will be allowed to connect with you again, but they will not automatically return to your crew."
        confirmText={busyId ? "Unblocking..." : "Unblock"}
        cancelText="Cancel"
        isDarkMode={isDarkMode}
      />

      <ConfirmationModal
        isOpen={!!confirmCancel}
        onClose={() => {
          if (!busyId) setConfirmCancel(null);
        }}
        onConfirm={() => {
          void confirmCancelCrewRequest();
        }}
        title={confirmCancel ? `Cancel request to ${confirmCancel.name}?` : "Cancel crew request?"}
        message="The pending Crew request will be withdrawn. You can send a new request later."
        confirmText={busyId ? "Cancelling..." : "Cancel request"}
        cancelText="Keep request"
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default CrewSettingsView;
